// =====================================================================
// LA FOTO DE PERFIL, ANTES DE MANDARLA · CUENTA A (19 sep 2026)
// =====================================================================
// Dos funciones puras que la pantalla usa y las pruebas ejercitan solas:
//
//   prepararFoto(archivo)        → { blob, ancho, alto } o { error }
//   subirConProgreso(ruta, blob, alAvanzar) → respuesta JSON del servidor
//
// POR QUÉ SE REDUCE AQUÍ Y NO SOLO EN EL SERVIDOR. La primera clienta
// reportó que «Seleccionar archivo» no hacía nada. Medido: el servidor
// funciona (201 y la foto servida), pero una foto de cámara de hoy pesa
// 3–8 MB, el tope es 2 MB, y el rechazo se pintaba diez líneas más abajo
// del control, fuera de la pantalla de un teléfono. Reducir en el
// navegador a 1024 px hace que CUALQUIER foto de cámara quepa, y de paso
// tira los metadatos (posición GPS, modelo del teléfono) antes de que
// salgan del aparato. El servidor sigue validando por contenido y
// recortando a 256 px: esto es comodidad, no la guarda.
import { ErrorApi } from '../api';

export const LADO_MAXIMO = 1024;
export const TOPE_BYTES = 2 * 1024 * 1024;
const TIPOS = new Set(['image/jpeg', 'image/png', 'image/webp']);

// Lee los primeros bytes: JPG, PNG o WebP DE VERDAD, no por la extensión.
export function tipoPorContenido(bytes) {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  if (b.length >= 3 && b[0] === 0xFF && b[1] === 0xD8 && b[2] === 0xFF) return 'image/jpeg';
  if (b.length >= 8 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4E && b[3] === 0x47) return 'image/png';
  if (b.length >= 12 && b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46
      && b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50) return 'image/webp';
  return null;
}

export function mensajeDeTamano(bytes) {
  return `La foto pesa ${(bytes / 1048576).toFixed(1)} MB y el máximo es 2 MB. Elige otra o recórtala en tu teléfono.`;
}

async function decodificar(archivo) {
  if (typeof createImageBitmap === 'function') {
    try { return await createImageBitmap(archivo); } catch { /* se intenta con <img> */ }
  }
  if (typeof Image === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) return null;
  return new Promise((resolver) => {
    const url = URL.createObjectURL(archivo);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolver(img); };
    img.onerror = () => { URL.revokeObjectURL(url); resolver(null); };
    img.src = url;
  });
}

/** Devuelve `{ blob, ancho, alto, reducida }` lista para subir, o `{ error }` con la frase para la persona. */
export async function prepararFoto(archivo, { ladoMaximo = LADO_MAXIMO } = {}) {
  if (!archivo) return { error: 'Elige una foto para subirla.' };
  const cabecera = new Uint8Array(await archivo.slice(0, 16).arrayBuffer());
  const tipo = tipoPorContenido(cabecera);
  if (!tipo || !TIPOS.has(tipo)) {
    return { error: 'Ese archivo no es una foto JPG, PNG o WebP. Elige una foto de tu galería o toma una con la cámara.' };
  }
  const imagen = await decodificar(archivo);
  if (!imagen) {
    // Sin motor de imágenes (pruebas, navegador muy viejo): se manda tal
    // cual y el servidor decide, como hasta ahora.
    if (archivo.size > TOPE_BYTES) return { error: mensajeDeTamano(archivo.size) };
    return { blob: archivo, ancho: null, alto: null, reducida: false };
  }
  const ancho = imagen.naturalWidth || imagen.width;
  const alto = imagen.naturalHeight || imagen.height;
  if (!ancho || !alto) return { error: 'La foto está vacía o dañada. Elige otra.' };
  const cabe = ancho <= ladoMaximo && alto <= ladoMaximo && archivo.size <= TOPE_BYTES;
  const puedeDibujar = typeof document !== 'undefined' && document.createElement;
  if (cabe && tipo === 'image/jpeg') return { blob: archivo, ancho, alto, reducida: false };
  if (!puedeDibujar) {
    if (archivo.size > TOPE_BYTES) return { error: mensajeDeTamano(archivo.size) };
    return { blob: archivo, ancho, alto, reducida: false };
  }
  const escala = Math.min(1, ladoMaximo / Math.max(ancho, alto));
  const w = Math.max(1, Math.round(ancho * escala));
  const h = Math.max(1, Math.round(alto * escala));
  const lienzo = document.createElement('canvas');
  lienzo.width = w; lienzo.height = h;
  const ctx = lienzo.getContext && lienzo.getContext('2d');
  if (!ctx) {
    if (archivo.size > TOPE_BYTES) return { error: mensajeDeTamano(archivo.size) };
    return { blob: archivo, ancho, alto, reducida: false };
  }
  ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, h); // un PNG transparente no sale negro
  ctx.drawImage(imagen, 0, 0, w, h);
  if (imagen.close) imagen.close();
  const blob = await new Promise((r) => lienzo.toBlob(r, 'image/jpeg', 0.86));
  if (!blob) {
    if (archivo.size > TOPE_BYTES) return { error: mensajeDeTamano(archivo.size) };
    return { blob: archivo, ancho, alto, reducida: false };
  }
  if (blob.size > TOPE_BYTES) return { error: mensajeDeTamano(blob.size) };
  return { blob, ancho: w, alto: h, reducida: true };
}

/** POST multipart con barra de avance. `alAvanzar(0..100)`. Mismo contrato de error que `api.subir`. */
export function subirConProgreso(ruta, blob, alAvanzar = () => {}, { campo = 'archivo', nombre = 'foto.jpg', crearXhr = () => new XMLHttpRequest() } = {}) {
  return new Promise((resolver, rechazar) => {
    const xhr = crearXhr();
    xhr.open('POST', `/api${ruta}`);
    xhr.responseType = 'text';
    if (xhr.upload) {
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) alAvanzar(Math.round((e.loaded / e.total) * 100)); };
    }
    xhr.onerror = () => rechazar(new ErrorApi('No hubo conexión con el servidor. Revisa la red y vuelve a intentarlo.', 0));
    xhr.onload = () => {
      let cuerpo = null;
      try { cuerpo = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch { cuerpo = null; }
      if (xhr.status >= 200 && xhr.status < 300) { alAvanzar(100); resolver(cuerpo); return; }
      const detalle = (cuerpo && (cuerpo.detalle || cuerpo.detail))
        || (xhr.status === 413 ? mensajeDeTamano(blob.size)
          : xhr.status >= 500 ? 'El servidor no contestó. Espera unos segundos y vuelve a intentarlo.'
            : 'No se pudo guardar la foto, y el servidor no dijo por qué. Vuelve a intentarlo.');
      rechazar(new ErrorApi(typeof detalle === 'string' ? detalle : 'No se pudo guardar la foto.', xhr.status));
    };
    const datos = new FormData();
    datos.append(campo, blob, nombre);
    xhr.send(datos);
  });
}

// La cabecera se entera sin recargar: `auth.jsx` escucha este evento.
export const EVENTO_FOTO = 'tego:foto-de-perfil';
export function avisarCambioDeFoto(tieneFoto) {
  if (typeof window === 'undefined' || !window.dispatchEvent) return;
  window.dispatchEvent(new CustomEvent(EVENTO_FOTO, { detail: { tiene_foto: !!tieneFoto, sello: Date.now() } }));
}
